import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Plus, Trash2, Tag } from 'lucide-react';
import { getTags } from '../../actions/tagActions';
import QuestionFormSkeleton from './views/FormView/QuestionFormSkeleton';

const QuestionForm = ({ mode, question, onSubmit, onClose }) => {
  const dispatch = useDispatch();
  const { tags } = useSelector(state => state.tag);
  const [formData, setFormData] = useState({
    text: '',
    options: [
      { text: '', value: 0 },
      { text: '', value: 0 }
    ],
    tags: []
  });
  const [isLoading, setIsLoading] = useState(true);

  // Tag-related state
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const tagDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    dispatch(getTags());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoading(true);
      try {
        if (question && mode !== 'new') {
          setFormData({
            text: question.text || '',
            options: question.options || [{ text: '', value: 0 }, { text: '', value: 0 }],
            tags: question.tags ? question.tags.map(tag => tag._id) : []
          });
        } else {
          setFormData({
            text: '',
            options: [
              { text: '', value: 0 },
              { text: '', value: 0 }
            ],
            tags: []
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [question, mode]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Tag helper functions
  const toggleTag = (tagId) => {
    const newTags = formData.tags.includes(tagId)
      ? formData.tags.filter(id => id !== tagId)
      : [...formData.tags, tagId];
    setFormData({ ...formData, tags: newTags });
    setTagSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeTag = (tagId) => {
    const newTags = formData.tags.filter(id => id !== tagId);
    setFormData({ ...formData, tags: newTags });
  };

  const getSelectedTagObjects = () => {
    return tags.filter(tag => formData.tags.includes(tag._id));
  };

  const getFilteredTags = () => {
    return tags.filter(tag => 
      !formData.tags.includes(tag._id) && 
      tag.title.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  };

  const handleTagInputClick = () => {
    setShowTagDropdown(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Backspace' && tagSearchTerm === '' && formData.tags.length > 0) {
      removeTag(formData.tags[formData.tags.length - 1]);
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { text: '', value: 0 }]
    });
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData({ ...formData, options: newOptions });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isView = mode === 'view';
  const isNew = mode === 'new';
  const isEdit = mode === 'edit';

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-[#054581]">
          {isNew && 'Add New Question'}
          {isEdit && 'Edit Question'}
          {isView && 'View Question'}
        </h3>
        <button
          onClick={onClose}
          type="button"
          className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {isLoading ? (
        <QuestionFormSkeleton />
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="text" className="block text-sm font-medium text-[#054581] mb-2">
              Question Text
            </label>
            <textarea
              id="text"
              name="text"
              value={formData.text}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors resize-none"
              rows="3"
              placeholder="Enter the question text..."
              required
              readOnly={isView}
            />
          </div>

          {/* Tags Section */}
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Tags
            </label>
            {!isView && (
              <div className="relative" ref={tagDropdownRef}>
                <div
                  onClick={handleTagInputClick}
                  className="min-h-[42px] px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md cursor-text focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:border-transparent transition-all"
                >
                  <div className="flex flex-wrap items-center gap-1">
                    {getSelectedTagObjects().map(tag => (
                      <span
                        key={tag._id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-20 text-[#1086a4] border border-[#1086a4] border-opacity-30"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag.title}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTag(tag._id);
                          }}
                          className="ml-1 text-[#1086a4] hover:text-[#054581] hover:bg-[#1086a4] hover:bg-opacity-20 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={tagSearchTerm}
                      onChange={(e) => setTagSearchTerm(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      onFocus={() => setShowTagDropdown(true)}
                      placeholder={formData.tags.length === 0 ? "Search and select tags..." : "Search tags..."}
                      className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm"
                    />
                  </div>
                </div>
                {showTagDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-[#1086a4] rounded-md shadow-lg max-h-48 overflow-auto">
                    {getFilteredTags().length > 0 ? (
                      getFilteredTags().map(tag => (
                        <button
                          key={tag._id}
                          type="button"
                          onClick={() => toggleTag(tag._id)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center"
                        >
                          <Tag className="w-3 h-3 mr-2 text-[#1086a4]" />
                          {tag.title}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        {tagSearchTerm ? `No tags found for "${tagSearchTerm}"` : 'No more tags available'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {isView && getSelectedTagObjects().length > 0 && (
              <div className="flex flex-wrap gap-2">
                {getSelectedTagObjects().map(tag => (
                  <span
                    key={tag._id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag.title}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-[#054581]">
                Answer Options
              </label>
              {!isView && (
                <button
                  type="button"
                  onClick={addOption}
                  className="flex items-center gap-1 px-2 py-1 text-sm text-[#1086a4] hover:text-[#054581] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Option
                </button>
              )}
            </div>
            <div className="space-y-3">
              {formData.options.map((option, index) => (
                <div key={index} className="flex gap-3 items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                      className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                      placeholder={`Option ${index + 1} text...`}
                      required
                      readOnly={isView}
                    />
                  </div>
                  <div className="w-24">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={option.value}
                      onChange={(e) => handleOptionChange(index, 'value', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                      placeholder="0-10"
                      required
                      readOnly={isView}
                    />
                  </div>
                  {!isView && formData.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors rounded-full hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
            >
              Cancel
            </button>
            {!isView && (
              <button
                type="submit"
                className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                {isNew ? 'Add Question' : 'Update Question'}
              </button>
            )}
          </div>
        </div>
      )}
    </form>
  );
};

export default QuestionForm;
